import type { GameState } from '../types';
import type { ValidAction } from '../../multiplayer/types';
import { actionToId } from '../core/actions';
import { BeginnerAIStrategy, RandomAIStrategy } from './strategies';
import type { AIActionAnalysis, AIStrategy } from './types';
import { createSeededRng, type RandomGenerator } from './hand-sampler';
import type { MonteCarloConfig } from './monte-carlo';
import {
  effectiveProfile,
  isInternalAIConfig,
  type InternalSerializableAIConfig,
  type SerializableAIConfig,
} from './difficulty-types';
import { ProfiledAIStrategy } from './profiled-strategy';
import { resolveBehaviorModel } from './adaptive-learning.js';

/** Internal harness strategy types — not the product API. */
export type AIStrategyType = 'beginner' | 'random';

/**
 * Configuration for AI strategy - all dependencies injected, no globals.
 */
export interface AIStrategyConfig {
  type: AIStrategyType;
  /** RNG for random strategy (defaults to Math.random) */
  rng?: RandomGenerator;
  /** Monte Carlo config for beginner strategy */
  monteCarloConfig?: Partial<MonteCarloConfig>;
}

export {
  effectiveProfile,
  isInternalAIConfig,
  type AdaptiveProfileSnapshot,
  type InternalSerializableAIConfig,
  type ProductSerializableAIConfig,
  type SerializableAIConfig,
  type SerializableDifficultyProfile,
} from './difficulty-types';

export interface ResolvedAIStrategyConfig {
  strategy: AIStrategy;
  profileId?: string;
}

export type AIActionSelection = AIActionAnalysis;

function mixRngSeed(seed: number, value: number): number {
  let mixed = (seed ^ value) >>> 0;
  mixed = Math.imul(mixed ^ (mixed >>> 16), 0x7feb352d);
  mixed = Math.imul(mixed ^ (mixed >>> 15), 0x846ca68b);
  return (mixed ^ (mixed >>> 16)) >>> 0;
}

/**
 * Derive deterministic randomness for one event-sourced decision.
 * Replaying the same position produces the same choice, while later turns,
 * seats, and hands do not restart the match-wide random stream.
 */
export function deriveDecisionRngSeed(
  baseSeed: number,
  state: Pick<GameState, 'shuffleSeed' | 'actionHistory'>,
  playerId: number,
): number {
  let seed = mixRngSeed(baseSeed, state.shuffleSeed);
  seed = mixRngSeed(seed, playerId);
  for (const action of state.actionHistory) {
    const actionId = actionToId(action);
    seed = mixRngSeed(seed, actionId.length);
    for (let index = 0; index < actionId.length; index += 1) {
      seed = mixRngSeed(seed, actionId.charCodeAt(index));
    }
  }
  return seed;
}

export function toAIStrategyConfig(
  config: SerializableAIConfig,
): ResolvedAIStrategyConfig {
  const rng = createSeededRng(config.aiRngSeed);

  if (isInternalAIConfig(config)) {
    return {
      strategy: createInternalStrategy(config, rng),
      profileId: config.legacyStrategy,
    };
  }

  const profile = effectiveProfile(config);
  const adaptive = config.adaptiveProfile;
  const behaviorModel = resolveBehaviorModel(adaptive?.behaviorModel);

  const strategyOptions: import('./profiled-strategy.js').ProfiledStrategyOptions =
    {
      profile,
      rng,
      useBehaviorWeights: profile.useBehaviorWeights,
      adaptiveConfidence: adaptive?.confidence ?? 0,
      adaptiveCompletedHands: adaptive?.completedHands ?? 0,
    };

  if (behaviorModel) {
    strategyOptions.behaviorModel = behaviorModel;
  }

  return {
    strategy: new ProfiledAIStrategy(strategyOptions),
    profileId: profile.id,
  };
}

function createInternalStrategy(
  config: InternalSerializableAIConfig,
  rng: RandomGenerator,
): AIStrategy {
  switch (config.legacyStrategy) {
    case 'random':
      return new RandomAIStrategy(rng);
    case 'beginner':
      return new BeginnerAIStrategy({
        ...config.monteCarloConfig,
        seed: config.aiRngSeed,
      });
  }
}

/**
 * Create a strategy instance from internal harness config.
 */
export function createStrategy(config: AIStrategyConfig): AIStrategy {
  switch (config.type) {
    case 'random':
      return new RandomAIStrategy(config.rng);
    case 'beginner':
      return new BeginnerAIStrategy(config.monteCarloConfig);
  }
}

/**
 * Pure function - Select AI action for a player.
 * All configuration is passed explicitly - no global state.
 */
export function selectAIAction(
  state: GameState,
  playerId: number,
  validActions: ValidAction[],
  config: SerializableAIConfig | AIStrategyConfig,
): ValidAction | null {
  return selectAIActionAnalysis(state, playerId, validActions, config)?.action ?? null;
}

export function selectAIActionAnalysis(
  state: GameState,
  playerId: number,
  validActions: ValidAction[],
  config: SerializableAIConfig | AIStrategyConfig,
  options: { deadlineAt?: number } = {},
): AIActionSelection | null {
  const myActions = validActions.filter((validAction) => {
    if (!('player' in validAction.action)) {
      return true;
    }
    return validAction.action.player === playerId;
  });

  if (myActions.length === 0) return null;

  const strategy =
    'legacyStrategy' in config || 'profile' in config
      ? toAIStrategyConfig({
          ...(config as SerializableAIConfig),
          aiRngSeed: deriveDecisionRngSeed(
            (config as SerializableAIConfig).aiRngSeed,
            state,
            playerId,
          ),
        }).strategy
      : createStrategy(config as AIStrategyConfig);

  const analysis = strategy.analyzeAction?.(state, myActions, options) ?? {
    action: strategy.chooseAction(state, myActions),
    status: 'scored' as const,
  };
  assertLegalAction(analysis.action, myActions);
  return analysis;
}

export function assertLegalAction(
  chosen: ValidAction | null,
  validActions: ValidAction[],
): asserts chosen is ValidAction {
  if (!chosen) {
    throw new Error('selectAIAction: strategy returned null');
  }

  const isLegal = validActions.some(
    (validAction) =>
      actionToId(validAction.action) === actionToId(chosen.action),
  );
  if (!isLegal) {
    throw new Error('selectAIAction: strategy returned illegal action');
  }
}
